import type { PlatformTheme, PlatformThemeColors, PlatformThemeGradients } from './types';
import { ensureReadableText } from './contrast';

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function darken(hex: string, amount = 0.15): string {
  const [r, g, b] = hexToRgb(hex);
  const f = 1 - amount;
  const to = (v: number) => Math.round(v * f);
  return `#${[to(r), to(g), to(b)].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

function lighten(hex: string, amount = 0.12): string {
  const [r, g, b] = hexToRgb(hex);
  const f = (v: number) => Math.round(v + (255 - v) * amount);
  return `#${[f(r), f(g), f(b)].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

export function buildThemeGradients(colors: PlatformThemeColors): PlatformThemeGradients {
  const accent = colors.accentPrimary;
  const accent2 = colors.accentSecondary;
  const bg = colors.background;
  const surface = colors.surface;
  return {
    primary: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
    secondary: `linear-gradient(135deg, ${accent2} 0%, ${accent} 100%)`,
    background: `linear-gradient(180deg, ${bg} 0%, ${surface} 100%)`,
    banner: `linear-gradient(135deg, ${accent} 0%, ${accent2} 55%, ${darken(accent2, 0.2)} 100%)`,
    avatar: `linear-gradient(145deg, ${accent} 0%, ${lighten(accent, 0.08)} 50%, ${accent2} 100%)`,
  };
}

export type ThemeEffect = 'glass' | 'glow';

/** Concise per-preset input for buildThemeFromSeed — a platform's controller defines a SEEDS
 * array of these (name/description/two accent colors/light-or-dark/optional effect) instead of
 * hand-authoring full 16-field color objects for every one of its 50 presets. */
export type ThemeSeed = {
  name: string;
  description: string;
  accent: string;
  accent2: string;
  /** Defaults to true (dark theme). Set false for a light-background variant. */
  dark?: boolean;
  /** Omit for a plain "classic" preset; 'glass' or 'glow' opts into that effect (see
   * PlatformTheme.cssVariables in types.ts). */
  effect?: ThemeEffect;
  glassAlpha?: string;
  glassBlur?: string;
};

function darkColorsFor(seed: ThemeSeed): PlatformThemeColors {
  return {
    background: '#0d0d0d',
    surface: '#1a1a1a',
    surface2: '#222222',
    surface3: '#2a2a2a',
    border: '#2a2a2a',
    text: '#f0f0f0',
    textMuted: '#888888',
    textDim: '#555555',
    accentPrimary: seed.accent,
    accentSecondary: seed.accent2,
    accentHover: darken(seed.accent, 0.08),
    headerBackground: '#1a1a1a',
    sidebarBackground: '#1a1a1a',
    success: seed.accent.startsWith('#00') ? seed.accent : '#00b894',
    warning: '#fdcb6e',
    error: '#d63031',
  };
}

function lightColorsFor(seed: ThemeSeed): PlatformThemeColors {
  return {
    background: '#f4f4f8',
    surface: '#ffffff',
    surface2: '#ececf2',
    surface3: '#e0e0ea',
    border: '#d8d8e4',
    text: '#1a1a2e',
    textMuted: '#5a5a72',
    textDim: '#8a8aa0',
    accentPrimary: seed.accent,
    accentSecondary: seed.accent2,
    accentHover: darken(seed.accent, 0.1),
    headerBackground: '#ffffff',
    sidebarBackground: '#f0f0f6',
    success: '#00b894',
    warning: '#e17055',
    error: '#d63031',
  };
}

function buildDarkTheme(seed: ThemeSeed): PlatformTheme {
  const colors = darkColorsFor(seed);
  return { name: seed.name, description: seed.description, colors, gradients: buildThemeGradients(colors) };
}

function buildLightTheme(seed: ThemeSeed): PlatformTheme {
  const colors = lightColorsFor(seed);
  return { name: seed.name, description: seed.description, colors, gradients: buildThemeGradients(colors) };
}

/** Generates a full PlatformTheme (colors + gradients + effect cssVariables) from a concise seed.
 * `text`/`textMuted` are run through ensureReadableText against the seed's own surface color, so
 * a preset can never accidentally ship illegible text — same runtime safety net applyPlatformTheme
 * applies again for custom/user-edited themes. */
export function buildThemeFromSeed(seed: ThemeSeed): PlatformTheme {
  const colors = seed.dark === false ? lightColorsFor(seed) : darkColorsFor(seed);
  colors.text = ensureReadableText(colors.surface, colors.text);
  colors.textMuted = ensureReadableText(colors.surface, colors.textMuted);
  const gradients = buildThemeGradients(colors);
  const cssVariables: Record<string, string> | undefined = seed.effect
    ? {
      'platform-effect': seed.effect,
      ...(seed.effect === 'glass'
        ? { 'platform-glass-alpha': seed.glassAlpha ?? '0.55', 'platform-glass-blur': seed.glassBlur ?? '18px' }
        : {}),
    }
    : undefined;
  return { name: seed.name, description: seed.description, colors, gradients, cssVariables };
}

export function buildThemesFromSeeds(seeds: ThemeSeed[]): PlatformTheme[] {
  return seeds.map(buildThemeFromSeed);
}

const SEEDS: ThemeSeed[] = [
  { name: 'ToHear Default', description: 'Signature teal podcast studio', accent: '#00b894', accent2: '#6c5ce7' },
  { name: 'PlatzView Default', description: 'Signature pink video studio', accent: '#e84393', accent2: '#6c5ce7' },
  { name: 'Dark', description: 'Classic dark with blue accents', accent: '#0984e3', accent2: '#6c5ce7' },
  { name: 'Light', description: 'Clean light workspace', accent: '#075e54', accent2: '#25d366', dark: false },
  { name: 'Ocean', description: 'Deep ocean blues and teals', accent: '#00bcd4', accent2: '#0288d1' },
  { name: 'Forest', description: 'Natural greens and earthy tones', accent: '#4caf50', accent2: '#81c784' },
  { name: 'Sunset', description: 'Warm oranges and purples', accent: '#ff6d00', accent2: '#e84393' },
  { name: 'Midnight', description: 'Ultra dark with neon accents', accent: '#00e5ff', accent2: '#76ff03' },
  { name: 'Lavender', description: 'Soft purples and lilacs', accent: '#9c27b0', accent2: '#ce93d8', dark: false },
  { name: 'Crimson', description: 'Bold reds and deep maroons', accent: '#e53935', accent2: '#ff5252' },
  { name: 'Emerald', description: 'Rich emerald greens', accent: '#00b894', accent2: '#55efc4' },
  { name: 'Arctic', description: 'Icy blues and cool whites', accent: '#74b9ff', accent2: '#a29bfe', dark: false },
  { name: 'Sapphire', description: 'Royal blue elegance', accent: '#1565c0', accent2: '#42a5f5' },
  { name: 'Rose Gold', description: 'Warm rose and gold tones', accent: '#e84393', accent2: '#fdcb6e', dark: false },
  { name: 'Slate', description: 'Neutral slate and steel', accent: '#636e72', accent2: '#b2bec3' },
  { name: 'Tokyo Neon', description: 'Cyberpunk neon on dark', accent: '#ff00ff', accent2: '#00ffff' },
  { name: 'Desert Sand', description: 'Warm sand and amber', accent: '#e17055', accent2: '#fdcb6e', dark: false },
  { name: 'Royal Grape', description: 'Deep grape and violet', accent: '#6c5ce7', accent2: '#a29bfe' },
  { name: 'Fresh Mint', description: 'Cool mint and aqua', accent: '#00cec9', accent2: '#81ecec', dark: false },
  { name: 'Graphite', description: 'Monochrome graphite', accent: '#dfe6e9', accent2: '#b2bec3' },
];

export const DEFAULT_PLATFORM_THEMES: PlatformTheme[] = SEEDS.map(seed =>
  seed.dark === false ? buildLightTheme(seed) : buildDarkTheme(seed),
);

export const PLATFORM_DEFAULT_THEME: Record<string, string> = {
  tohear: 'ToHear Default',
  platzview: 'PlatzView Default',
  dating: 'Rose Gold',
  greetings: 'Sunset',
  sound: 'Midnight',
  'ai-music': 'Emerald',
  'ai-image-video': 'Sapphire',
  saas: 'Royal Grape',
  cut: 'Tokyo Neon',
  tradalous: 'Forest',
  generic: 'Dark',
};

export function findBuiltinThemeByName(name: string): PlatformTheme {
  const found = DEFAULT_PLATFORM_THEMES.find(t => t.name === name);
  return found ?? DEFAULT_PLATFORM_THEMES[0];
}

export function getDefaultThemeName(platformId = 'generic'): string {
  return PLATFORM_DEFAULT_THEME[platformId] ?? PLATFORM_DEFAULT_THEME.generic;
}

/** Client-side built-in presets (always available without API). */
export function getBuiltinThemes(): PlatformTheme[] {
  return DEFAULT_PLATFORM_THEMES;
}
